import { SerializedConstructor } from '@langchain/core/load/serializable';

import { FC } from 'react';

import { isPlaygroundTools } from '@/utils/ai-tools';

import { supportsEnforcedToolSelection } from '../../utils/Playground.utils';
import { manifestModelAndProvider } from '../manifest/serialized/ManifestConstructor';
import GenericToolChoiceSelect from '../manifest/serialized/common/GenericToolChoiceSelect';
import { ManifestOpenAIParallelToolCalls } from '../manifest/serialized/common/ManifestCommonChatOpenAI';
import {
  DEFAULT_TOOL_CHOICE,
  PROVIDER_TOOL_CHOICES,
  SUPPORTS_PARALLEL_TOOL_CALLS,
} from '../manifest/serialized/common/constants';
import SettingsModalSection from './SettingsModalSection';

type ToolSettingsProps = {
  manifest: SerializedConstructor;
  setManifest: (manifest: SerializedConstructor) => void;
  setEdited: (edited: boolean) => void;
  options: any;
  setOptions: (options: any) => void;
  onSettingsChange?: () => void;
};

const ToolSettingsSection: FC<ToolSettingsProps> = ({
  manifest,
  setManifest,
  setEdited,
  options,
  setOptions,
  onSettingsChange,
}) => {
  const manifestLast = manifest.kwargs.last;

  const modelAndProvider = manifestModelAndProvider({
    variant: 'line',
    value: manifestLast,
    options: {},
  });
  const provider = modelAndProvider?.provider;
  if (!provider) return null;

  return (
    <SettingsModalSection
      title="Tool Settings"
      subtitle="Define how tools are used in your prompt."
    >
      <div className="flex flex-col gap-4">
        {SUPPORTS_PARALLEL_TOOL_CALLS.includes(provider) && (
          <ManifestOpenAIParallelToolCalls
            options={options}
            onOptionsChange={setOptions}
          />
        )}
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium">Tool Choice</div>
          <GenericToolChoiceSelect
            variant="pane"
            value={manifest.kwargs.first}
            onChange={(value) => {
              setManifest({
                ...manifest,
                kwargs: { ...manifest.kwargs, first: value },
              });
              setEdited(true);
              onSettingsChange?.();
            }}
            options={options}
            onOptionsChange={(newOptions) => {
              setEdited(true);
              setOptions(newOptions);
              onSettingsChange?.();
            }}
            provider={provider}
            tools={isPlaygroundTools(options?.tools) ? options?.tools : []}
            toolChoices={
              PROVIDER_TOOL_CHOICES[provider] ?? [DEFAULT_TOOL_CHOICE]
            }
            toolPrefix={
              ['AzureChatOpenAI', 'ChatAnthropic'].includes(provider)
                ? 'tool'
                : 'func'
            }
            disableSpecificToolChoice={
              !['AzureChatOpenAI', 'ChatAnthropic'].includes(provider)
                ? !supportsEnforcedToolSelection(modelAndProvider) &&
                  modelAndProvider?.provider !== 'AzureChatOpenAI'
                : undefined
            }
          />
        </div>
      </div>
    </SettingsModalSection>
  );
};

export default ToolSettingsSection;
